// 頁面路由表（AppLayout 底下的子路由），路徑與舊版 crm/src/routes.js 一致。每頁 lazy 載入，切成獨立 chunk。
// 跟 router.jsx 分開，AppLayout 才能引用它判斷「已知路徑」而不循環引用。新頁面只在這裡加。
import { matchRoutes, Navigate } from 'react-router'
import { RouteFallback } from './RouteFallback'
import { account } from '@/lib/auth'

const page = (load) => async () => ({ Component: await load() })

const DashboardPage = page(() => import('@/features/dashboard/pages/DashboardPage').then((m) => m.DashboardPage))
const TasksPage = page(() => import('@/features/tasks/pages/TasksPage').then((m) => m.TasksPage))
const CreateTeamTaskPage = page(() => import('@/features/tasks/pages/CreateTeamTaskPage').then((m) => m.CreateTeamTaskPage))
const MerchantsPage = page(() => import('@/features/merchants/pages/MerchantsPage').then((m) => m.MerchantsPage))
const MerchantPage = page(() => import('@/features/merchant/pages/MerchantPage').then((m) => m.MerchantPage))
const LinesPage = page(() => import('@/features/lines/pages/LinesPage').then((m) => m.LinesPage))
const TagsManagePage = page(() => import('@/features/tags/pages/TagsManagePage').then((m) => m.TagsManagePage))
const LinePage = page(() => import('@/features/line/pages/LinePage').then((m) => m.LinePage))
const CampaignManagementPage = page(() => import('@/features/campaigns/pages/CampaignManagementPage').then((m) => m.CampaignManagementPage))
const CampaignDetailPage = page(() => import('@/features/campaigns/pages/CampaignDetailPage').then((m) => m.CampaignDetailPage))
const ProductsPage = page(() => import('@/features/products/pages/ProductsPage').then((m) => m.ProductsPage))
const MessageInboxPage = page(() => import('@/features/message-inbox/pages/MessageInboxPage').then((m) => m.MessageInboxPage))
const FeedbacksPage = page(() => import('@/features/feedbacks/pages/FeedbacksPage').then((m) => m.FeedbacksPage))
const GamesPage = page(() => import('@/features/games/pages/GamesPage').then((m) => m.GamesPage))
const AccountPermissionsPage = page(() => import('@/features/account-permissions/pages/AccountPermissionsPage').then((m) => m.AccountPermissionsPage))
const KitchenPage = page(() => import('@/features/dev/pages/KitchenPage').then((m) => m.KitchenPage))

// 元件規格總覽的開放環境，與後端 isNonProductionEnv() 同一條政策：只開 dev / testing。
// 刻意用白名單而不是 !== 'prod'：ENV 沒設或拼錯時要落在「不開」這一邊。
// env 由後端注入 window.user，lib/auth 再以 hostname 補（補的是 'Dev'／'Stg.' 這種大小寫，所以正規化後再比）。
// ⚠️ runtime 判斷沒有 tree-shake，頁面必須維持 lazy chunk，否則 shiki 會被打進首屏
const DEV_PAGE_ENVS = ['dev', 'testing']
const showDevPages = import.meta.env.DEV || DEV_PAGE_ENVS.includes(String(account.env ?? '').trim().toLowerCase())

export const pageRoutes = [
  { path: '/', element: <Navigate to="/home" replace /> },
  { path: '/home', lazy: DashboardPage },
  { path: '/dashboard', lazy: DashboardPage },
  { path: '/tasks', lazy: TasksPage },
  { path: '/tasks/ongoing', lazy: TasksPage },
  { path: '/tasks/history', lazy: TasksPage },
  { path: '/tasks/drafts', lazy: TasksPage },
  { path: '/tasks/notes', lazy: TasksPage },
  { path: '/tasks/create', lazy: CreateTeamTaskPage },
  { path: '/tasks/edit', lazy: CreateTeamTaskPage },
  { path: '/merchants', lazy: MerchantsPage },
  { path: '/merchants/financial-groupings', lazy: MerchantsPage },
  { path: '/merchants/secondary-agents', lazy: MerchantsPage },
  { path: '/merchants/pricing-approvals', lazy: MerchantsPage },
  { path: '/merchants/:code', lazy: MerchantPage },
  { path: '/merchants/:code/contacts', lazy: MerchantPage },
  { path: '/merchants/:code/pricing', lazy: MerchantPage },
  { path: '/merchants/:code/lines', lazy: MerchantPage },
  { path: '/merchants/:code/timeline', lazy: MerchantPage },
  { path: '/merchants/:code/contracts', lazy: MerchantPage },
  { path: '/lines', lazy: LinesPage },
  { path: '/tags-manage', lazy: TagsManagePage },
  { path: '/lines/:pid', lazy: LinePage },
  { path: '/lines/:pid/product-pricing', lazy: LinePage },
  { path: '/lines/:pid/monthly-bet', lazy: LinePage },
  { path: '/lines/:pid/products', lazy: LinePage },
  { path: '/lines/:pid/timeline', lazy: LinePage },
  { path: '/lines/:pid/accounts', lazy: LinePage },
  { path: '/lines/:pid/documents', lazy: LinePage },
  { path: '/lines/:pid/related-tasks', lazy: LinePage },
  { path: '/lines/:pid/screenshots', lazy: LinePage },
  { path: '/campaigns', lazy: CampaignManagementPage },
  { path: '/campaigns/:id', lazy: CampaignDetailPage },
  { path: '/products', lazy: ProductsPage },
  { path: '/products/providers', lazy: ProductsPage },
  { path: '/products/features', lazy: ProductsPage },
  { path: '/message-inbox', lazy: MessageInboxPage },
  { path: '/feedbacks', lazy: FeedbacksPage },
  { path: '/games', lazy: GamesPage },
  { path: '/account-permissions', lazy: AccountPermissionsPage },
  { path: '/oplogs', lazy: page(() => import('@/features/oplogs/pages/OplogsPage').then((m) => m.OplogsPage)) },
  ...(showDevPages ? [{ path: '/dev/components', lazy: KitchenPage }] : []),
  { path: '*', element: <RouteFallback /> },
]

// '*' 萬用路由不算已知。比對分大小寫（同舊版 knownPathPatterns 的 RegExp 無 i 旗標）：
// 頁面渲染照 router 預設不分大小寫（舊版 Solid router 也不分），只有導航守衛與「最後有效路徑」認大小寫
const knownRoutes = pageRoutes.map((r) => ({ path: r.path, caseSensitive: true }))
export function isKnownPath(pathname) {
  const matches = matchRoutes(knownRoutes, pathname)
  return Boolean(matches) && matches.at(-1).route.path !== '*'
}
